import React, { useState, useEffect } from 'react';

import { Tooltip, Fab } from "@mui/material";
import Grid from '@mui/material/Grid2';
import {
  DeleteForever, Add
} from "@mui/icons-material";

import { 
  CreatureLimitation,
  Potence,
  TransformOption,
} from "@dndModels";
import { 
  CHARACTER_LEVELS,
} from "@dndModels/Constants";

import SelectStringBox from "@components/input/selects/SelectStringBox";
import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";

import DisplayObjects from "@dndComponents/display/DisplayObjects";

import SelectObjectBox from "@components/input/selects/SelectObjectBoxSimple";
import CreatureLimitInput from "@dndComponents/limitations/CreatureLimitInput";

interface Props {
  obj: Potence;
  slot_level: number;
  onChange: (changed: Potence) => void;
  onDelete: () => void;
  disabled?: boolean;
}

export default function AbilityPotenceTransformInput({ 
  obj,
  slot_level,
  onChange,
  onDelete,
  disabled = false 
}: Props) {
  const [expanded_option, setExpandedOption] = useState("");
  const [reloading, setReloading] = useState(false);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  return (
    <Grid container spacing={0} direction="row">
      <Grid
        size={{
          xs: 4
        }}>
        <SelectStringBox 
          options={CHARACTER_LEVELS}
          value={`${obj.level}`} 
          name="Level"
          disabled={disabled}
          onChange={(changed: string) => {
            const new_obj = obj;
            new_obj.level = +changed;
            onChange(new_obj);
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 4
        }}>
        <span className="header">
          Options
        </span>
        { !disabled &&
          <Tooltip title={`Add Option`}>
            <Fab size="small" color="primary" style={{marginLeft: "8px"}}
              onClick={ () => {
                const new_obj = obj;
                new_obj.transform_options.push(new TransformOption());
                onChange(new_obj);
              }}>
              <Add/>
            </Fab>
          </Tooltip>
        }
      </Grid>
      <Grid
        size={{
          xs: 4
        }}>
        { !disabled &&
          <Tooltip title={`Delete Potence`}>
            <Fab size="small" color="primary" style={{marginLeft: "8px"}}
              onClick={ () => {
                onDelete();
              }}>
              <DeleteForever/>
            </Fab>
          </Tooltip>
        }
      </Grid>
      { !reloading && obj.transform_options.map((option, key) => {
        return (
          <Grid
            key={key}
            container
            spacing={0}
            direction="row"
            size={{
              xs: 12
            }}>
            <Grid
              size={{
                xs: 2
              }}>
              <ToggleButtonBox
                name="Expand"
                value={option.true_id === expanded_option}
                onToggle={() => {
                  setExpandedOption(expanded_option === option.true_id ? "" : option.true_id);
                }}
              />
            </Grid>
            { option.true_id === expanded_option ?
              <>
                <Grid
                  size={{
                    xs: 8
                  }}>
                  <SelectObjectBox
                    name="Using Transform"
                    data_type="transform"
                    value={option.transform_id}
                    disabled={disabled}
                    onChange={(changed: string) => {
                      option.transform_id = changed;
                      const new_obj = obj;
                      onChange(new_obj);
                    }}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 2
                  }}>
                  { !disabled &&
                    <Tooltip title={`Delete Option`}>
                      <Fab size="small" color="primary" style={{marginLeft: "8px"}}
                        onClick={ () => {
                          const new_obj = obj;
                          new_obj.transform_options = new_obj.transform_options.filter(o => o.true_id !== option.true_id);
                          onChange(new_obj);
                          setReloading(true);
                        }}>
                        <DeleteForever/>
                      </Fab>
                    </Tooltip>
                  }
                </Grid>
                <Grid
                  size={{
                    xs: 12
                  }}>
                  <CreatureLimitInput
                    obj={option.creature_limit}
                    disabled={disabled}
                    onChange={(changed: CreatureLimitation) => {
                      option.creature_limit = changed.clone();
                      const new_obj = obj;
                      onChange(new_obj);
                    }}
                  />
                </Grid>
              </>
            :
              <Grid
                size={{
                  xs: 10
                }}>
                { option.transform_id === "" ? "No Transform" : <DisplayObjects type="transform" ids={[option.transform_id]} /> }:&nbsp;
                { option.creature_limit.creature_ids.length > 0 && <DisplayObjects type="creature" ids={option.creature_limit.creature_ids} /> }
              </Grid>
            }
          </Grid>
        );
      })}
    </Grid>
  );
}
